import { useEffect, useRef, useState } from 'react';
import { Play } from 'lucide-react';
import { trackNewsletterAction } from '@/lib/subscribeFunnelAnalytics';
import poster from '../assets/promo/gamify-your-newsletter-poster.webp';
import poster480 from '../assets/promo/gamify-your-newsletter-poster-480w.webp';
import poster768 from '../assets/promo/gamify-your-newsletter-poster-768w.webp';
import poster1152 from '../assets/promo/gamify-your-newsletter-poster-1152w.webp';

const VIDEO_ID = 'ExsL_il2il8';
const VIDEO_TITLE = 'Gamify Your Newsletter with Atrivial';

/**
 * The promo video, shown as its end-card poster until the visitor asks to play it. YouTube loads
 * only after that click; before the page hydrates, the poster is a plain link to the video.
 */
export default function PromoVideo() {
  const [playing, setPlaying] = useState(false);
  const player = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (playing) player.current?.focus();
  }, [playing]);

  return (
    <section
      data-newsletter-section="promo_video"
      className="bg-nl-paper px-6 pt-16 pb-24 md:pt-24"
    >
      <div className="relative mx-auto aspect-video max-w-6xl overflow-hidden rounded-2xl border border-nl-line bg-nl-muted shadow-nl-pop">
        {playing ? (
          <iframe
            ref={player}
            className="absolute inset-0 h-full w-full border-0"
            src={`https://www.youtube.com/embed/${VIDEO_ID}?autoplay=1&rel=0`}
            title={VIDEO_TITLE}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        ) : (
          <a
            href={`https://www.youtube.com/watch?v=${VIDEO_ID}`}
            className="group absolute inset-0 block focus-visible:outline-3 focus-visible:-outline-offset-4 focus-visible:outline-nl-brand"
            aria-label={`Play the video ${VIDEO_TITLE}`}
            onClick={(event) => {
              event.preventDefault();
              trackNewsletterAction('promo_video_play');
              setPlaying(true);
            }}
          >
            <img
              src={poster}
              srcSet={`${poster480} 480w, ${poster768} 768w, ${poster1152} 1152w, ${poster} 1920w`}
              sizes="(min-width: 1200px) 1152px, calc(100vw - 48px)"
              alt=""
              width={1920}
              height={1080}
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover"
            />
            <span
              aria-hidden="true"
              className="absolute bottom-4 left-4 grid size-12 place-items-center rounded-full bg-nl-brand text-nl-on-brand shadow-nl-pop transition-transform duration-fast group-hover:-translate-y-0.5 motion-reduce:transition-none motion-reduce:group-hover:translate-y-0 md:bottom-7 md:left-7 md:size-16"
            >
              <Play className="ml-0.5 size-5 fill-current md:size-7" />
            </span>
          </a>
        )}
      </div>
    </section>
  );
}
